/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  PlusCircle,
  Building2,
  User,
  Shield,
  Lock,
  Activity,
} from 'lucide-react';
import { userService } from '../services/userService';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { FIELD_LIMITS, validateUsername } from '../utils/constants';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const USERS_ROUTE = '/users';

const NewUser = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);

  const [form, setForm] = useState({
    email: '',
    username: '',
    externalUserId: '',
    firstName: '',
    lastName: '',
    displayName: '',
    password: '',
    status: 'active',
    roleKeys: [],
    accountUID: '',
    subAccountUID: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const isPlatformAdmin = currentUser?.scope === 'platform';
  const isAccountAdmin = currentUser?.scope === 'account';
  const currentScope = currentUser?.scope || 'platform';

  // Filter roles based on current context
  const filteredRoles = useMemo(() => {
    // Filter out platform_admin role unless the current user is a platform_admin
    const isPlatformAdminRole = currentUser?.role === 'platform_admin';

    let filtered = roles;

    // Hide platform_admin role from everyone except platform_admin users
    if (!isPlatformAdminRole) {
      filtered = filtered.filter(r => r.id !== 'PLATFORM_ADMIN' && r.key !== 'Platform Admin');
    }

    // Platform admin with no context: show all roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered;
    }

    // Platform admin in account context: show account and sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === 'account' || r.rawScope === 'sub_account');
    }

    // Platform admin in sub-account context: show only sub-account roles
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === 'sub_account');
    }

    // Account admin: show account and sub-account roles
    if (isAccountAdmin) {
      return filtered.filter(r => r.rawScope === 'account' || r.rawScope === 'sub_account');
    }

    // Sub-account admin: show only sub-account roles
    if (currentScope === 'sub_account') {
      return filtered.filter(r => r.rawScope === 'sub_account');
    }

    return filtered;
  }, [roles, currentScope, isPlatformAdmin, isAccountAdmin, currentUser?.role, currentUser?.accountUID, currentUser?.subAccountUID]);

  // Derive the scope required by selected roles
  const requiredScope = useMemo(() => {
    if (!form.roleKeys.length) { return null; }
    const selected = filteredRoles.filter(r => form.roleKeys.includes(r.id));
    if (selected.some(r => r.rawScope === 'sub_account')) { return 'sub_account'; }
    if (selected.some(r => r.rawScope === 'account')) { return 'account'; }
    return 'platform';
  }, [form.roleKeys, filteredRoles]);

  // Determine if dropdowns should be shown based on context and role requirements
  const needsAccount = isPlatformAdmin && !currentUser?.accountUID && (requiredScope === 'account' || requiredScope === 'sub_account');

  const needsSubAccount = (() => {
    // Platform admin in platform context: show dropdown for sub-account roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === 'sub_account';
    }
    // Platform admin with account context: show dropdown for sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === 'sub_account';
    }
    // Platform admin with sub-account context: never show sub-account dropdown
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return false;
    }
    // Account admin: show dropdown for sub-account roles
    if (isAccountAdmin) {
      return requiredScope === 'sub_account';
    }
    return false;
  })();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const scopeLabelMap = {
          account: ACCOUNT_LABEL.singular,
          platform: 'Platform',
          sub_account: SUB_ACCOUNT_LABEL.singular,
        };
        const res = await userService.getRoles();
        const rolesData = res.data?.roles || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setRoles(rolesData.map(r => ({
          id: r.key || r.name?.toUpperCase() || r.id,
          key: r.name,
          displayName: r.displayName || r.name,
          module: r.scope === 'sub_account' ? SUB_ACCOUNT_LABEL.singular : scopeLabelMap[r.scope],
          rawScope: r.scope,
          description: r.description,
        })));
      } catch {
        toastService.error('You are not authorized to view roles.');
      }
    };
    fetchRoles();
  }, []);

  // Load accounts for platform admin when account picker appears
  useEffect(() => {
    if (!needsAccount || accounts.length) { return; }
    apiClient.get('/accounts', { params: { limit: 100, page: 1, status: 'active' } })
      .then(({ data }) => { setAccounts(data.data || []); })
      .catch(() => toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`));
  }, [needsAccount, accounts.length]);

  // Load sub-accounts when account is selected or from context
  useEffect(() => {
    if (!needsSubAccount) { setSubAccounts([]); return; }

    let accountId;
    if (isPlatformAdmin && !currentUser?.accountUID) {
      // Platform admin in platform context: use selected account
      accountId = form.accountUID;
    } else if (isPlatformAdmin && currentUser?.accountUID) {
      // Platform admin with account context: use context account
      accountId = currentUser.accountUID;
    } else if (isAccountAdmin) {
      // Account admin: use their account
      accountId = currentUser?.accountUID;
    } else {
      // Sub-account admin: use their account
    }

    if (!accountId) { setSubAccounts([]); return; }
    apiClient.get(`/accounts/${accountId}/sub-accounts`, { params: { limit: 100, page: 1, status: 'active' } })
      .then(({ data }) => setSubAccounts(data.subAccounts || data.data || []))
      .catch(() => toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`));
  }, [needsSubAccount, form.accountUID, currentUser?.accountUID, isPlatformAdmin, isAccountAdmin]);

  // Reset account/sub-account when scope changes
  useEffect(() => {
    setForm(f => ({ ...f, accountUID: '', subAccountUID: '' }));
    setSubAccounts([]);
  }, [requiredScope]);

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\S{8,128}$/;

  const validatePassword = (password) => {
    if (!password?.trim()) {
      return null;
    }

    return PASSWORD_REGEX.test(password)
      ? null
      : 'Password must be between 8 and 128 characters long and include at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.';
  };

  const validateForm = () => {
    const errors = {};
    if (!form.email) { errors.email = 'Email is required'; }
    else if (!isEmail(form.email)) { errors.email = 'Invalid email format'; }

    const usernameError = validateUsername(form.username);
    if (usernameError) { errors.username = usernameError; }

    if (!form.firstName.trim()) { errors.firstName = 'First name is required'; }
    if (!form.lastName.trim()) { errors.lastName = 'Last name is required'; }
    if (!form.displayName.trim()) { errors.displayName = 'Display name is required'; }
    const passwordError = validatePassword(form.password);
    if (passwordError) { errors.password = passwordError; }
    if (!form.roleKeys.length) { errors.roleKeys = 'At least one role is required'; }

    // Only validate account selection if dropdown is shown
    if (needsAccount && !form.accountUID && !currentUser?.accountUID) {
      errors.accountUID = `${ACCOUNT_LABEL.singular} is required for this role`;
    }

    // Only validate sub-account selection if dropdown is shown
    if (needsSubAccount && !form.subAccountUID && !currentUser?.subAccountUID) {
      errors.subAccountUID = `${SUB_ACCOUNT_LABEL.singular} is required for this role`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (['firstName', 'lastName', 'displayName', 'username'].includes(field) && typeof value === 'string' && value !== value.trim()) {
      value = value.trim();
      setForm(f => ({ ...f, [field]: value }));
    }
    if (field === 'username') {
      // Optional field — only validate the format when a value is entered
      setFieldErrors(prev => ({ ...prev, username: validateUsername(value) }));
      return;
    }
    if (!value || !String(value).trim()) {
      const labels = { firstName: 'First name', lastName: 'Last name', displayName: 'Display name', email: 'Email', password: 'Password', roleKeys: 'Role', accountUID: ACCOUNT_LABEL.singular, subAccountUID: SUB_ACCOUNT_LABEL.singular };
      setFieldErrors(prev => ({ ...prev, [field]: `${labels[field] || field} is required` }));
    } else if (field === 'email' && !isEmail(value)) {
      setFieldErrors(prev => ({ ...prev, email: 'Invalid email format' }));
    } else if (field === 'password') {
      const error = validatePassword(value);
      if (error) setFieldErrors(prev => ({ ...prev, password: error }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      const payload = {
        email: form.email,
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: form.displayName.trim(),
        roleKeys: form.roleKeys,
        status: form.status,
      };
      const username = form.username.trim();
      if (username) payload.username = username;
      const externalUserId = form.externalUserId.trim();
      if (externalUserId) payload.externalUserId = externalUserId;

      // Add accountUID based on context and role requirements
      if (requiredScope === 'account' || requiredScope === 'sub_account') {
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected account
          payload.accountUID = form.accountUID;
        } else if (isPlatformAdmin && currentUser?.accountUID) {
          // Platform admin with account context: use context account
          payload.accountUID = currentUser.accountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account
          payload.accountUID = currentUser?.accountUID;
        } else if (currentScope === 'sub_account') {
          // Sub-account admin: use their account
          payload.accountUID = currentUser?.accountUID;
        } else {
          payload.accountUID = null;
        }
      }

      // Add subAccountUID based on context and role requirements
      if (requiredScope === 'sub_account') {
        if (isPlatformAdmin && currentUser?.subAccountUID) {
          // Platform admin with sub-account context: use context sub-account
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (currentScope === 'sub_account' && currentUser?.subAccountUID) {
          // Sub-account admin: use their sub-account
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (form.subAccountUID) {
          // Otherwise use selected sub-account
          payload.subAccountUID = form.subAccountUID;
        } else {
          payload.subAccountUID = null;
        }
      }

      await userService.createUser(payload);
      toastService.success('User created successfully');
      navigate(USERS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to create user'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(USERS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Users
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                New Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Create User
            </h1>
            <p className="text-xs text-base-content/50">
              Select a security role, configure organization scope, and provide user details.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Role, Organization & Scope */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Building2 size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Organization & Scope
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Role Selection (Always First) */}
            <div className={`space-y-1.5 ${(!needsAccount && !needsSubAccount) ? 'sm:col-span-2' : ''}`}>
              <label htmlFor="role" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Shield size={12} className="text-primary" />
                <span>Role *</span>
              </label>
              <select
                id="role"
                className={selectClass('roleKeys')}
                value={form.roleKeys[0] || ''}
                onBlur={e => handleBlur('roleKeys', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, roleKeys: e.target.value ? [e.target.value] : [] })); setFieldErrors(p => ({ ...p, roleKeys: null })); }}
              >
                <option value="">— Select a role —</option>
                {filteredRoles.sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })).map(role => (
                  <option key={role.id} value={role.id}>
                    {role.displayName} ({role.module})
                  </option>
                ))}
              </select>
              {fieldErrors.roleKeys && <p className="text-error text-xs font-medium">{fieldErrors.roleKeys}</p>}
            </div>

            {/* Account Selection (Shown when role requires account scope) */}
            {needsAccount && (
              <div className="space-y-1.5">
                <label htmlFor="account" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  {ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="account"
                  className={selectClass('accountUID')}
                  value={form.accountUID}
                  onBlur={e => handleBlur('accountUID', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, accountUID: e.target.value, subAccountUID: '' })); setFieldErrors(p => ({ ...p, accountUID: null })); }}
                >
                  <option value="">{`— Select ${ACCOUNT_LABEL.withArticle} —`}</option>
                  {accounts.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                {fieldErrors.accountUID && <p className="text-error text-xs font-medium">{fieldErrors.accountUID}</p>}
              </div>
            )}

            {/* Sub-Account Selection (Shown when role requires sub-account scope) */}
            {needsSubAccount && (
              <div className="space-y-1.5">
                <label htmlFor="subAccount" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  {SUB_ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="subAccount"
                  className={selectClass('subAccountUID')}
                  value={form.subAccountUID}
                  disabled={isPlatformAdmin && !currentUser?.accountUID && !form.accountUID}
                  onBlur={e => handleBlur('subAccountUID', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, subAccountUID: e.target.value })); setFieldErrors(p => ({ ...p, subAccountUID: null })); }}
                >
                  <option value="">{isPlatformAdmin && !currentUser?.accountUID && !form.accountUID ? `— Select ${ACCOUNT_LABEL.withArticle} first —` : `— Select ${SUB_ACCOUNT_LABEL.withArticle} —`}</option>
                  {subAccounts.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {fieldErrors.subAccountUID && <p className="text-error text-xs font-medium">{fieldErrors.subAccountUID}</p>}
              </div>
            )}
          </div>
        </div>

        {/* User Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <User size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              User Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-1.5">
              <label htmlFor="firstName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                maxLength={30}
                placeholder="e.g. John"
                className={inputClass('firstName')}
                value={form.firstName}
                onBlur={e => handleBlur('firstName', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, firstName: e.target.value })); setFieldErrors(p => ({ ...p, firstName: null })); }}
              />
              {fieldErrors.firstName && <p className="text-error text-xs font-medium">{fieldErrors.firstName}</p>}
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label htmlFor="lastName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                maxLength={30}
                placeholder="e.g. Doe"
                className={inputClass('lastName')}
                value={form.lastName}
                onBlur={e => handleBlur('lastName', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, lastName: e.target.value })); setFieldErrors(p => ({ ...p, lastName: null })); }}
              />
              {fieldErrors.lastName && <p className="text-error text-xs font-medium">{fieldErrors.lastName}</p>}
            </div>

            {/* Email Address */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                maxLength={100}
                placeholder="john.doe@company.com"
                className={inputClass('email')}
                value={form.email}
                onBlur={e => handleBlur('email', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setFieldErrors(p => ({ ...p, email: null })); }}
              />
              {fieldErrors.email && <p className="text-error text-xs font-medium">{fieldErrors.email}</p>}
            </div>

            {/* Display Name */}
            <div className="space-y-1.5">
              <label htmlFor="displayName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Display Name *
              </label>
              <input
                id="displayName"
                type="text"
                maxLength={30}
                placeholder="e.g. John D."
                className={inputClass('displayName')}
                value={form.displayName}
                onBlur={e => handleBlur('displayName', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, displayName: e.target.value })); setFieldErrors(p => ({ ...p, displayName: null })); }}
              />
              {fieldErrors.displayName && <p className="text-error text-xs font-medium">{fieldErrors.displayName}</p>}
            </div>

            {/* Username */}
            <div className="space-y-1.5">
              <label htmlFor="username" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="off"
                maxLength={FIELD_LIMITS.USERNAME_MAX_LENGTH}
                placeholder="Optional, e.g. john.doe"
                className={inputClass('username')}
                value={form.username}
                onBlur={e => handleBlur('username', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, username: e.target.value })); setFieldErrors(p => ({ ...p, username: null })); }}
              />
              {fieldErrors.username
                ? <p className="text-error text-xs font-medium">{fieldErrors.username}</p>
                : <p className="text-base-content/40 text-xs">Can be used to sign in. Cannot be changed once set.</p>}
            </div>

            {/* External User ID */}
            <div className="space-y-1.5">
              <label htmlFor="externalUserId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                External User ID
              </label>
              <input
                id="externalUserId"
                type="text"
                maxLength={FIELD_LIMITS.EXTERNAL_ID}
                placeholder="Optional"
                className={inputClass('externalUserId')}
                value={form.externalUserId}
                onChange={e => setForm(f => ({ ...f, externalUserId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Security & Access */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Lock size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Security & Status
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Lock size={12} className="text-primary" />
                <span>Password *</span>
              </label>
              <input
                id="password"
                type="password"
                maxLength={128}
                autoComplete="new-password"
                name="new-password"
                placeholder="••••••••"
                className={inputClass('password')}
                value={form.password}
                onBlur={e => handleBlur('password', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setFieldErrors(p => ({ ...p, password: null })); }}
              />
              {fieldErrors.password && <p className="text-error text-xs font-medium">{fieldErrors.password}</p>}
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <label htmlFor="status" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Activity size={12} className="text-primary" />
                <span>Status</span>
              </label>
              <div className="flex items-center gap-4 px-4 py-2.5 bg-base-100 border border-base-300 rounded-xl h-[42px]">
                <label aria-label="User Status Toggle" className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="status"
                    className="sr-only peer"
                    checked={form.status === 'active'}
                    onChange={e => setForm(f => ({ ...f, status: e.target.checked ? 'active' : 'inactive' }))}
                  />
                  <div className="w-10 h-5 bg-base-content/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
                <label htmlFor="status" className="text-xs font-bold text-base-content/70 cursor-pointer whitespace-nowrap">
                  {form.status === 'active' ? 'Active' : 'Inactive'}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(USERS_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating User...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create User</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewUser;
